<script lang="ts">
export default {
  name: 'KkBox',
}
</script>

<script lang="ts" setup>
import { computed, useSlots } from 'vue'

export interface Props {
  tag?: string
  level?: 1 | 2 | 3 | 4 | 5 | 6
  title?: string
  note?: string
  contentHeight?: string
  headerBottomBorder?: boolean
  border?: boolean
  shadow?: boolean
  grayContent?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  tag: 'section',
  level: 2,
  headerBottomBorder: true,
})

const slots = useSlots()

const hasHeader = computed(() => {
  return Object.prototype.hasOwnProperty.call(slots, 'header')
})

const hasFooter = computed(() => {
  return Object.prototype.hasOwnProperty.call(slots, 'footer')
})

const styles = computed(() => {
  const styles: Record<string, string> = {}
  if (props.contentHeight) {
    styles['max-height'] = props.contentHeight
    styles['overflow-y'] = 'auto'
  }
  return styles
})

const classes = computed(() => {
  const classes = ['box']
  if (props.border) classes.push('--border')
  if (props.shadow) classes.push('--shadow')
  return classes
})

const headerClasses = computed(() => {
  const classes = ['box__header']
  if (props.headerBottomBorder) classes.push('--border')
  return classes
})

const contentClasses = computed(() => {
  const classes = ['box__content']
  if (props.grayContent) classes.push('--gray')
  return classes
})
</script>

<template>
  <component :is="props.tag" :class="classes">
    <header v-if="hasHeader || title" :class="headerClasses" class="box__header">
      <component :is="`h${ level }`" v-if="title" class="box__title">
        {{ title }}
        <span v-if="note" class="box__title__note">
          {{ note }}
        </span>
      </component>
      <slot v-if="hasHeader" name="header" />
    </header>
    <div :class="contentClasses" class="box__content" :style="styles">
      <slot name="content" />
    </div>
    <footer v-if="hasFooter" class="box__footer">
      <slot name="footer" />
    </footer>
  </component>
</template>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/_var.scss";
@import "../../../../../stylesheets/biz_spa/foundation/_mixin.scss";
.box {
  display: flex;
  flex-direction: column;
  max-height: 100%;
  border-radius: 4px;
  box-shadow: 0 0 0 rgb(63 63 68 / 5%);
  background-color: #FFF;
  &__header {
    padding: 16px;
    &.--border {
      border-bottom: 1px solid #E4E7EB;
    }
  }
  &__title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    font-weight: bold;
    font-size: 20px;
    line-height: 1.25;
    gap: 16px;
    &__note {
      margin-top: .2em;
      font-weight: normal;
      font-size: 12px;
    }
  }
  &__content {
    padding: 16px;
    &.--gray {
      background-color: #F8F9FA;
    }
  }
  &__footer {
    padding: 16px;
    border-top: 1px solid #E4E7EB;
  }
  &.--border {
    border: 1px solid #E4E7EB;
  }
  &.--shadow {
    box-shadow: 0 1px 4px rgba(0, 0, 0, .26);
  }
  @include mq() {
    &__header,
    &__content,
    &__footer {
      padding: 24px 32px;
    }
  }
}
</style>
